import { MarketLink } from '@market/react';
import { useMemo, type MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetMerchantDefaultBrandResponse } from '@squareup/shared-types-protos/squareup/cash/local/v1/seller/cash_app_local_seller_service';
import styles from './profile-info.module.css';

export interface ProfileInfoProps {
  defaultBrandData?: GetMerchantDefaultBrandResponse | null;
  onEditProfileClick?: () => void;
}

export function ProfileInfo({
  defaultBrandData,
  onEditProfileClick,
}: ProfileInfoProps) {
  const { t } = useTranslation();

  const brandName = defaultBrandData?.name || '';
  const profileUrl = defaultBrandData?.profileUrl || '';
  const displayUrl = defaultBrandData?.profileUrl
    ? defaultBrandData.profileUrl.replace('https://', '').replace('order/', '')
    : '';
  const profileIcon = defaultBrandData?.artworkImage?.lightUrl || '';
  const backgroundColorLight = defaultBrandData?.backgroundColor?.light?.srgb || '#FFF';
  const outlineColorLight = defaultBrandData?.brandCardOutlineColor?.light?.srgb;

  // Generate profile initials from brand name
  const profileInitials = useMemo(() => {
    if (!brandName) {
      return '';
    }

    const words = brandName.trim().split(/\s+/);

    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }

    if (words.length === 1 && words[0].length >= 2) {
      return words[0].slice(0, 2).toUpperCase();
    }

    return words[0] || '';
  }, [brandName]);

  const handleEditProfileClick: MouseEventHandler<HTMLMarketLinkElement> = (e) => {
    e.preventDefault();
    onEditProfileClick?.();
  };

  return (
    <div className={styles.profileInfoSection}>
      <div
        className={styles.profileIconContainer}
        style={{ background: backgroundColorLight, ...(outlineColorLight && { boxShadow: `inset 0 0 0 0.5px ${outlineColorLight}` }) }}
        data-testid="profile-icon-container"
      >
        <div className={styles.profileIcon}>
          {profileIcon
            ? (
              <img src={profileIcon} alt={brandName} />
            )
            : (
              <span>{profileInitials}</span>
            )}
        </div>
      </div>

      <span className={styles.profileInfoTitle}>{brandName}</span>

      {displayUrl && (
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.lightGreyText}
        >
          {displayUrl}
        </a>
      )}

      {onEditProfileClick && (
        <MarketLink
          href="#"
          className={styles.editProfileLink}
          onClick={handleEditProfileClick}
        >
          {t('profileDropdown.profile.editProfile')}
        </MarketLink>
      )}
    </div>
  );
}

export default ProfileInfo;
